import { Component, OnInit, Input, Output, EventEmitter, AfterViewInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { StoreService, AppDataService } from 'app/services';
import { LocationType } from 'app/models';
import { MerchantStoreOnboardingComponent } from 'app/merchant';
import { PartnerMartLocationsStoreDetailsComponent } from 'app/partner';

@Component({
    selector: 'merchant-create-store',
    templateUrl: './merchant-create-store.component.html',
    styleUrls: ['./merchant-create-store.component.scss',
        '../../../../../styles/sidebar.scss',
        '../../../../../styles/style-layout.scss'],
    providers: [MessageService]
})
export class MerchantCreateStoreComponent implements OnInit, AfterViewInit {
    public showMerchantCreateStoreSideBar: boolean = false;
    @Input()
    set _showMerchantCreateStoreSideBar(value: boolean) {
        this.showMerchantCreateStoreSideBar = value;
    }

    @Input() public merchantStoreOnboardingComponent!: MerchantStoreOnboardingComponent;
    @Input() public partnerMartLocationsStoreDetailsComponent!: PartnerMartLocationsStoreDetailsComponent;

    @Output() changedShowMerchantCreateStoreSideBarEmit = new EventEmitter<boolean>();
    changedShowMerchantCreateStoreSideBar(increased: any) {
        this.changedShowMerchantCreateStoreSideBarEmit.emit(increased);
    }

    @Input()
    public editableStore: any;

    public merchantId: number = 0;

    constructor(private messageService: MessageService, private storeService: StoreService,
        public appData: AppDataService) {
        const reviewMerchantId = localStorage.getItem('reviewMerchantId');
        if (reviewMerchantId) {
            this.merchantId = JSON.parse(reviewMerchantId);
        } else {
            this.merchantId = parseInt(this.appData.authData.MerchantId, 10);
        }
    }

    public loading: boolean = false;

    public store: any = {};
    public locationType = LocationType.LocationTypeList;

    //Google maps autocomplete
    public map!: google.maps.Map;
    public geocoder!: google.maps.Geocoder;
    public marker!: google.maps.Marker;
    public addresses: any[] = [];
    private geocodeTimer: number = 0;

    ngOnInit(): void {
        if (this.editableStore) {
            this.store = Object.assign({}, this.editableStore);
            this.geocodeClick();
        }
    }

    ngAfterViewInit() {
        this.geocoder = new google.maps.Geocoder();
        this.map = new google.maps.Map(document.getElementById('map') as HTMLElement, {
            zoom: 15,
            center: { lat: 14.5995, lng: 120.9842 },
        });
        this.marker = new google.maps.Marker({
            map: this.map,
            anchorPoint: new google.maps.Point(0, -29),
        });
    }

    public geocodeClick() {
        if (this.geocodeTimer) {
            window.clearTimeout(this.geocodeTimer);
        }
        this.geocodeTimer = window.setTimeout(() => {
            this.geocoder.geocode({ address: this.store.Address },  (results, status) => {
                if (status === 'OK') {
                    if (results !== null) {
                        this.map.setCenter(results[0].geometry.location);
                        this.marker.setPosition(results[0].geometry.location);
                    }
                } else {
                    console.log('Geocode was not successful for the following reason: ' + status);
                }
            });
            this.geocodeTimer = 0;
        }, 700);
    }

    public createOrEditStore() {
        this.loading = true;

        const dto = {
            MerchantId: this.merchantId,
            Name: this.store.Name,
            Address: this.store.Address,
            AddressDetails: this.store.AddressDetails,
            LocationTypeId: this.store.LocationTypeId,
            ImageUrl: this.store.ImageUrl,
            Id: 0
        };

        if (!dto.MerchantId || !dto.Name || !dto.Address || !dto.AddressDetails || !dto.LocationTypeId || !dto.ImageUrl) {
            this.messageService.add({ severity: 'error', summary: 'Warning', detail: 'Fill all required fields.' });
            this.loading = false;
            return;
        }
        if (!this.editableStore) {
            this.storeService.createStore(dto, this.messageService).then(data => {
                if (!data) {
                    this.loading = false;
                    return;
                }

                if (!data.Success) {
                    this.loading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                if (this.merchantStoreOnboardingComponent !== null) {
                    this.merchantStoreOnboardingComponent.getMerchantUserAndMerchant();
                    this.merchantStoreOnboardingComponent.getStoresByMerchant();
                }
                if (this.partnerMartLocationsStoreDetailsComponent) {
                    this.partnerMartLocationsStoreDetailsComponent.getStore();
                }
                this.cancel();
                this.loading = false;
            });
        }
        else {
            dto.Id = this.editableStore.Id;
            this.storeService.editStore(dto, this.messageService).then(data => {
                if (!data) {
                    this.loading = false;
                    return;
                }

                if (!data.Success) {
                    this.loading = false;
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    return;
                }
                if (this.merchantStoreOnboardingComponent !== null) {
                    this.merchantStoreOnboardingComponent.getMerchantUserAndMerchant();
                    this.merchantStoreOnboardingComponent.getStoresByMerchant();
                }
                if (this.partnerMartLocationsStoreDetailsComponent) {
                    this.partnerMartLocationsStoreDetailsComponent.getStore();
                }
                this.cancel();
                this.loading = false;
            });
        }
    }

    public handleImageClick(id: string): void {
        document.getElementById(id)?.click();
    }

    public handleImageChange(event: Event) {
        const target = event.target as HTMLInputElement;

        let file: File | null = null;

        if (target.files) {
            file = target.files[0];
        }

        if (!file) {
            return;
        }

        // Chech max file size 2MB
        if (file.size > 2097152) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'This file is bigger than 2MB.' });
            return;
        }

        const reader = new FileReader();

        reader.onload = (e) => {
            if (e?.target?.result) {
                // Base64 string
                this.store.ImageUrl = e.target.result.toString();
            }
        };

        reader.readAsDataURL(file);
    }

    //Google maps autocomplete
    public search(event: any) {
        const query = event.query;
        const service = new google.maps.places.AutocompleteService();
        service.getPlacePredictions({ input: query }, (predictions, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
                this.addresses = predictions ? predictions.map((prediction) => prediction.description) : [];
            } else {
                this.addresses = [];
            }
        });
    }

    cancel() {
        this.changedShowMerchantCreateStoreSideBar(false);
    }
}
